import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '../core/Icon';
import { useCamada } from './useCamada';

export interface ModalProps {
    open?: boolean;
    title?: ReactNode;
    description?: ReactNode;
    width?: number | string;
    tone?: 'default' | 'danger';
    /** Há dado preenchido e não salvo: fechar passa a pedir confirmação. */
    dirty?: boolean;
    /**
     * "Salvar e fechar" da confirmação de descarte. O Modal ESPERA o retorno e
     * só fecha quando ele é `true`. Qualquer outro valor devolve a pessoa ao
     * formulário (a tela fecha sozinha quando der certo, ou mostra o erro de
     * validação dela); uma exceção aparece dentro da própria confirmação.
     */
    onSave?: () => unknown;
    saveLabel?: string;
    discardLabel?: string;
    children?: ReactNode;
    footer?: ReactNode;
    onClose?: () => void;
}

/**
 * Diálogo modal.
 *
 * Esc fecha. Quando há dado preenchido e não salvo (`dirty`), Esc, o ✕ e o
 * clique fora NÃO descartam direto: abrem uma confirmação. Fechar sem aviso é
 * perda de trabalho silenciosa, e num prontuário isso é perda de registro
 * clínico — quem digitou dez campos merece ser perguntado.
 *
 * Esc, trava de rolagem, foco que entra e volta e Tab circulando vêm de
 * `useCamada`, o mesmo das outras camadas modais.
 */
export function Modal({
    open = false, title, description, width = 480, tone = 'default',
    dirty = false, onSave, saveLabel = 'Salvar e fechar', discardLabel = 'Descartar alterações',
    children, footer, onClose,
}: ModalProps) {
    const [confirmando, setConfirmando] = useState(false);
    const [salvandoNaConfirmacao, setSalvandoNaConfirmacao] = useState(false);
    const [erroAoSalvar, setErroAoSalvar] = useState<string | null>(null);

    /* Pedido de fechamento passa sempre por aqui — teclado, ✕ ou clique fora. */
    const pedirFechamento = useCallback(() => {
        if (dirty) {
            setConfirmando(true);
            return;
        }
        onClose?.();
    }, [dirty, onClose]);

    useEffect(() => {
        if (!open) {
            setConfirmando(false);
            setErroAoSalvar(null);
        }
    }, [open]);

    const caixaRef = useCamada<HTMLDivElement>({
        aberta: open,
        /* Esc na confirmação volta para a edição, não fecha os dois de uma vez. */
        aoEsc: () => {
            if (confirmando) {
                setConfirmando(false);
                return;
            }
            pedirFechamento();
        },
    });

    if (!open) return null;

    const fecharAgora = () => {
        setConfirmando(false);
        setErroAoSalvar(null);
        onClose?.();
    };

    /* Fechar ANTES de saber se salvou era perda silenciosa: a validação recusava
       ou o servidor falhava, o diálogo já tinha sumido e o erro morria junto. */
    const salvarEFechar = async () => {
        if (!onSave || salvandoNaConfirmacao) return;
        setSalvandoNaConfirmacao(true);
        setErroAoSalvar(null);
        try {
            const resultado = await onSave();
            if (resultado === true) {
                fecharAgora();
                return;
            }
            setConfirmando(false);
        } catch (e) {
            setErroAoSalvar(e instanceof Error && e.message ? e.message : 'Não foi possível salvar. Tente de novo.');
        } finally {
            setSalvandoNaConfirmacao(false);
        }
    };

    const cabecalhoEstilo: CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '20px 22px 0' };

    /* Portal para o body. `position: fixed` ancora no ancestral com `transform`,
       e as telas usam transform na animação de entrada: sem portal, o diálogo
       saía da viewport e era encolhido pelo flex do contêiner errado. */
    const conteudo = (
        /* O fundo é afordância de clique-fora, não um controle: `presentation`
           diz isso à árvore de acessibilidade. O caminho de teclado equivalente
           é o Esc, tratado acima. */
        <div
            role="presentation"
            onClick={(e) => {
                /* Fecha só quando o clique foi no próprio fundo. Comparar alvo
                   com o elemento que ouve dispensa `stopPropagation` no diálogo
                   e nas camadas internas — que era um manipulador de clique num
                   `role="dialog"`, lido como controle improvisado. */
                if (e.target === e.currentTarget) pedirFechamento();
            }}
            style={{
                position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(17,19,24,.36)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
                animation: 'vec-fade var(--dur-base) var(--ease-standard)',
            }}
        >
            <div
                ref={caixaRef} role="dialog" aria-modal="true"
                aria-label={typeof title === 'string' ? title : undefined}
                tabIndex={-1}
                style={{
                    outline: 'none',
                    /* flexShrink 0: o diálogo tem largura declarada e não deve ser
                       comprimido pelo centralizador. */
                    width, maxWidth: '100%', flex: '0 0 auto', maxHeight: '86vh', overflow: 'auto',
                    background: 'var(--surface)', borderRadius: 'var(--radius-modal)', boxShadow: 'var(--shadow-xl)',
                    animation: 'vec-pop var(--dur-slow) var(--ease-entrance)',
                    position: 'relative',
                }}
            >
                <div style={cabecalhoEstilo}>
                    {tone === 'danger' && (
                        <span style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--danger-soft)', color: 'var(--danger-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                            <Icon name="triangle-alert" size={17} />
                        </span>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h2 style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 17, lineHeight: '24px', fontWeight: 600, letterSpacing: 'var(--tracking-snug)', color: 'var(--text-primary)' }}>{title}</h2>
                        {description && <p style={{ margin: '5px 0 0', fontSize: 13.5, lineHeight: '20px', color: 'var(--text-secondary)' }}>{description}</p>}
                    </div>
                    {dirty && (
                        <span title="Há alterações não salvas" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flex: '0 0 auto', height: 22, padding: '0 9px', borderRadius: 'var(--radius-pill)', background: 'var(--surface-brand-soft)', color: 'var(--text-brand)', fontFamily: 'var(--font-ui)', fontSize: 11.5, fontWeight: 600 }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--burgundy-500)' }} />
                            Não salvo
                        </span>
                    )}
                    <button type="button" onClick={pedirFechamento} aria-label="Fechar" style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--neutral-500)', padding: 2, flex: '0 0 auto' }}>
                        <Icon name="x" size={17} />
                    </button>
                </div>
                {children && <div style={{ padding: '16px 22px 0' }}>{children}</div>}
                {footer && <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '20px 22px 20px' }}>{footer}</div>}

                {/* Confirmação de descarte, sobreposta ao próprio diálogo: o conteúdo
                    digitado continua visível atrás, então a decisão é informada. */}
                {confirmando && (
                    <div
                        style={{ position: 'absolute', inset: 0, zIndex: 2, background: 'var(--surface-veil)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22, borderRadius: 'var(--radius-modal)' }}
                    >
                        <div role="alertdialog" aria-modal="true" aria-label="Fechar sem salvar?" style={{ width: '100%', maxWidth: 400, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', padding: 18 }}>
                            <div style={{ display: 'flex', gap: 11 }}>
                                <span style={{ width: 30, height: 30, flex: '0 0 auto', borderRadius: '50%', background: 'var(--warning-soft)', color: 'var(--warning-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Icon name="triangle-alert" size={16} />
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 14.5, fontWeight: 600, color: 'var(--text-primary)' }}>Fechar sem salvar?</p>
                                    <p style={{ margin: '4px 0 0', fontFamily: 'var(--font-ui)', fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                                        Há dados preenchidos que ainda não foram salvos. Se fechar agora, tudo o que você digitou é perdido.
                                    </p>
                                </div>
                            </div>
                            {erroAoSalvar && (
                                <p role="alert" style={{ margin: '12px 0 0', padding: '8px 10px', borderRadius: 'var(--radius-sm)', background: 'var(--danger-soft)', color: 'var(--danger-strong)', fontFamily: 'var(--font-ui)', fontSize: 13, lineHeight: '19px' }}>
                                    {erroAoSalvar}
                                </p>
                            )}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                                <BotaoConfirm variante="ghost" disabled={salvandoNaConfirmacao} onClick={() => { setConfirmando(false); setErroAoSalvar(null); }}>Continuar editando</BotaoConfirm>
                                <BotaoConfirm variante="danger" disabled={salvandoNaConfirmacao} onClick={fecharAgora}>{discardLabel}</BotaoConfirm>
                                {onSave && (
                                    <BotaoConfirm variante="primary" disabled={salvandoNaConfirmacao} onClick={() => void salvarEFechar()}>
                                        {salvandoNaConfirmacao ? 'Salvando…' : saveLabel}
                                    </BotaoConfirm>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <style>{'@keyframes vec-fade{from{opacity:0}to{opacity:1}}@keyframes vec-pop{from{opacity:0;transform:translateY(6px) scale(.99)}to{opacity:1;transform:none}}'}</style>
            </div>
        </div>
    );

    return typeof document !== 'undefined' ? createPortal(conteudo, document.body) : conteudo;
}

type VarianteConfirm = 'primary' | 'danger' | 'ghost';

/* Botão local: a confirmação vive dentro do Modal e não pode importar Button,
   que importaria Modal de volta. */
function BotaoConfirm({ variante = 'primary', onClick, disabled = false, children }: { variante?: VarianteConfirm; onClick?: () => void; disabled?: boolean; children?: ReactNode }) {
    const [hover, setHover] = useState(false);
    const paletas: Record<VarianteConfirm, CSSProperties> = {
        primary: { background: hover ? 'var(--action-primary-hover)' : 'var(--action-primary)', color: 'var(--action-primary-fg)', border: '1px solid transparent' },
        danger: { background: hover ? 'var(--danger-strong)' : 'var(--danger)', color: '#fff', border: '1px solid transparent' },
        ghost: { background: hover ? 'var(--surface-secondary)' : 'transparent', color: 'var(--neutral-700)', border: '1px solid transparent' },
    };
    return (
        <button
            type="button" onClick={onClick} disabled={disabled}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{
                height: 32, padding: '0 12px', borderRadius: 'var(--radius-control)', cursor: disabled ? 'progress' : 'pointer',
                opacity: disabled ? 0.7 : 1,
                fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, letterSpacing: 'var(--tracking-snug)',
                transition: 'var(--transition-control)', whiteSpace: 'nowrap', ...paletas[variante],
            }}
        >
            {children}
        </button>
    );
}
